<template>
  <!-- 쿠패스(COUPLUS WEIHAI) 상단 탭 바 + 탭별 화면 보관(keep-alive). 2026-10-04 YZY
       쿠패스 사이드바 메뉴(旧系统 포함)일 때만 보인다 — 셀러 화면은 Layout-1 의 기존 router-view 가 그대로 그린다. -->
  <div class="cp-tabs-view">
    <div class="cp-tabbar" v-if="active">
      <div
        v-for="tab in orderedTabs"
        :key="tab.name"
        class="cp-tab"
        :class="{ 'cp-tab--active': tab.name === activeName, 'cp-tab--pinned': tab.pinned }"
        :title="tab.title"
        @click="go(tab)"
        @mousedown.middle.prevent="close(tab)"
        @contextmenu.prevent="openMenu($event, tab)"
      >
        <span v-if="tab.pinned" class="cp-tab__pin">📌</span>
        <span class="cp-tab__title">{{ tab.title }}</span>
        <span v-if="canClose(tab)" class="cp-tab__close" title="关闭(닫기)" @click.stop="close(tab)">×</span>
      </div>
    </div>

    <div ref="views" class="cp-tab-views">
      <keep-alive>
        <router-view v-if="active" :key="viewKey" />
      </keep-alive>
    </div>

    <ul v-if="menu.show" class="cp-tabmenu" :style="{ left: menu.x + 'px', top: menu.y + 'px' }" @click.stop>
      <li v-if="canClose(menu.tab)" @click="menuAct('close')">关闭(닫기)</li>
      <li @click="menuAct('others')">关闭其他(다른 탭 닫기)</li>
      <li @click="menuAct('pin')">{{ menu.tab && menu.tab.pinned ? '取消固定(고정 해제)' : '固定(고정)' }}</li>
      <li @click="menuAct('reload')">刷新(새로고침)</li>
    </ul>
  </div>
</template>

<script>
import { isCoupassTabRoute, coupassTabTitle } from '@/views/backend/Logis/coupassMenu';

const PIN_KEY = 'coupassPinnedTabs';
const MAX_TABS = 12;

export default {
  name: 'CoupassTabsView',
  props: {
    // 지금 라우트가 쿠패스 탭 대상인가(Layout-1 이 판정)
    active: { type: Boolean, default: false }
  },
  data() {
    return {
      // { name, fullPath, title, pinned, nonce, lastActive } — nonce 는 「새로고침」 때 화면을 새로 만드는 데 쓴다
      tabs: [],
      // 지금 보이는 화면의 보관 키(라우트 이름#nonce). 탭 목록에서 계산하지 않는다 — 닫는 도중 키가 바뀌어 화면이 새로 만들어지지 않게
      viewKey: '',
      menu: { show: false, x: 0, y: 0, tab: null }
    };
  },
  computed: {
    activeName() {
      return this.active ? this.$route.name : '';
    },
    // 고정 탭이 맨 앞, 나머지는 연 순서
    orderedTabs() {
      return this.tabs.filter(t => t.pinned).concat(this.tabs.filter(t => !t.pinned));
    }
  },
  watch: {
    '$route'(to, from) {
      this.onRoute(to, from);
    }
  },
  created() {
    // 스크롤 위치는 탭별로 따로 둔다(화면 DOM 은 keep-alive 가 보관하지만 스크롤은 떼어낼 때 사라진다)
    this.scrollStore = {};
    this.restorePinned();
    this.onRoute(this.$route, null);
  },
  mounted() {
    window.addEventListener('scroll', this.onWindowScroll, { passive: true });
    this.$refs.views.addEventListener('scroll', this.onInnerScroll, true);
    document.addEventListener('click', this.closeMenu);
    document.addEventListener('keydown', this.onKeydown);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onWindowScroll);
    if (this.$refs.views) this.$refs.views.removeEventListener('scroll', this.onInnerScroll, true);
    document.removeEventListener('click', this.closeMenu);
    document.removeEventListener('keydown', this.onKeydown);
  },
  methods: {
    canClose(tab) {
      return !!tab && !tab.pinned && this.tabs.length > 1;
    },
    onRoute(to) {
      this.closeMenu();
      if (!isCoupassTabRoute(to.name)) return;
      let tab = this.tabs.find(t => t.name === to.name);
      if (tab) {
        tab.fullPath = to.fullPath;   // 탭 안에서 주소(?view= 등)가 바뀌어도 같은 탭
      } else {
        tab = { name: to.name, fullPath: to.fullPath, title: coupassTabTitle(to.name), pinned: false, nonce: 0, lastActive: 0 };
        this.tabs.push(tab);
        this.enforceLimit(tab);
      }
      tab.lastActive = Date.now();
      this.viewKey = tab.name + '#' + tab.nonce;
      if (tab.pinned) this.savePinned();
      this.$nextTick(() => {
        this.sweepCache();
        this.restoreScroll(tab.name);
      });
    },
    // 상한을 넘으면 가장 오래 안 본 비고정 탭부터 닫는다
    enforceLimit(keep) {
      while (this.tabs.length > MAX_TABS) {
        const victims = this.tabs.filter(t => !t.pinned && t !== keep).sort((a, b) => a.lastActive - b.lastActive);
        if (!victims.length) break;
        this.removeTab(victims[0]);
      }
    },
    go(tab) {
      this.closeMenu();
      if (tab.name === this.activeName) return;
      this.$router.push(tab.fullPath).catch(e => {
        if (e && e.name !== 'NavigationDuplicated') console.error('[쿠패스 탭] 이동 실패', e);
      });
    },
    removeTab(tab) {
      const i = this.tabs.indexOf(tab);
      if (i < 0) return;
      this.tabs.splice(i, 1);
      delete this.scrollStore[tab.name];
    },
    close(tab) {
      if (!this.canClose(tab)) return;
      const wasActive = tab.name === this.activeName;
      const order = this.orderedTabs;
      const idx = order.indexOf(tab);
      this.removeTab(tab);
      if (wasActive) {
        const next = order[idx + 1] || order[idx - 1];
        if (next) this.go(next);
      } else {
        this.$nextTick(this.sweepCache);
      }
    },
    closeOthers(tab) {
      this.tabs.filter(t => t !== tab && !t.pinned).forEach(t => this.removeTab(t));
      if (tab.name !== this.activeName) this.go(tab);
      else this.$nextTick(this.sweepCache);
    },
    togglePin(tab) {
      tab.pinned = !tab.pinned;
      this.savePinned();
    },
    // 화면을 새로 만든다(데이터 다시 불러오기). 지금 탭이면 바로, 아니면 다음에 열 때
    reload(tab) {
      tab.nonce += 1;
      delete this.scrollStore[tab.name];
      if (this.active && tab.name === this.$route.name) this.viewKey = tab.name + '#' + tab.nonce;
      this.$nextTick(this.sweepCache);
    },
    openMenu(e, tab) {
      this.menu = { show: true, x: e.clientX, y: e.clientY, tab };
    },
    closeMenu() {
      if (this.menu.show) this.menu = { show: false, x: 0, y: 0, tab: null };
    },
    onKeydown(e) {
      if (e.key === 'Escape') this.closeMenu();
    },
    menuAct(act) {
      const tab = this.menu.tab;
      this.closeMenu();
      if (!tab) return;
      if (act === 'close') this.close(tab);
      else if (act === 'others') this.closeOthers(tab);
      else if (act === 'pin') this.togglePin(tab);
      else if (act === 'reload') this.reload(tab);
    },
    // 열린 탭에 없는 보관 화면을 정리한다(닫은 탭·새로고침 전 화면). 지금 보이는 화면은 건드리지 않는다
    // keep-alive 는 추상 컴포넌트라 처음에 빈 채로 뜨면 ref 가 안 잡힌다(실측) — 보관 중인 화면에서 거슬러 찾는다
    keepAliveInstance() {
      const kid = this.$children.find(c => c.$vnode && c.$vnode.parent && c.$vnode.parent.componentInstance && c.$vnode.parent.componentInstance.cache);
      return kid ? kid.$vnode.parent.componentInstance : null;
    },
    sweepCache() {
      const ka = this.keepAliveInstance();
      if (!ka || !ka.cache || !ka.keys) return;
      const live = new Set(this.tabs.map(t => t.name + '#' + t.nonce));
      const current = this.active ? this.viewKey : null;
      Object.keys(ka.cache).forEach(key => {
        if (live.has(key) || key === current) return;
        const entry = ka.cache[key];
        delete ka.cache[key];
        const i = ka.keys.indexOf(key);
        if (i > -1) ka.keys.splice(i, 1);
        const inst = entry && entry.componentInstance;
        if (inst && !inst._isDestroyed) inst.$destroy();
      });
    },
    // ── 스크롤 — 창 스크롤 + 화면 안 스크롤 영역(표 등)을 탭별로 기억 ──
    scrollEntry(name) {
      if (!this.scrollStore[name]) this.scrollStore[name] = { y: 0, inner: new Map() };
      return this.scrollStore[name];
    },
    onWindowScroll() {
      if (!this.active) return;
      this.scrollEntry(this.$route.name).y = window.scrollY;
    },
    onInnerScroll(e) {
      if (!this.active || !e.target || e.target === document) return;
      this.scrollEntry(this.$route.name).inner.set(e.target, [e.target.scrollTop, e.target.scrollLeft]);
    },
    restoreScroll(name) {
      if (!this.active || this.$route.name !== name) return;
      const s = this.scrollStore[name];
      if (!s) { window.scrollTo(0, 0); return; }   // 처음 여는 탭은 맨 위부터
      s.inner.forEach((pos, el) => {
        if (!el.isConnected) { s.inner.delete(el); return; }
        el.scrollTop = pos[0];
        el.scrollLeft = pos[1];
      });
      window.scrollTo(0, s.y);
    },
    // ── 고정 탭 — 브라우저에 기억해 다음 접속 때 다시 연다 ──
    restorePinned() {
      let saved = [];
      try {
        saved = JSON.parse(localStorage.getItem(PIN_KEY) || '[]');
      } catch (e) {
        console.error('[쿠패스 탭] 고정 탭 불러오기 실패', e);
        saved = [];
      }
      if (!Array.isArray(saved)) return;
      saved.forEach(p => {
        if (!p || !isCoupassTabRoute(p.name) || this.tabs.some(t => t.name === p.name)) return;
        this.tabs.push({ name: p.name, fullPath: p.fullPath || '', title: coupassTabTitle(p.name), pinned: true, nonce: 0, lastActive: 0 });
      });
      // 저장된 주소가 비었거나 다른 라우트를 가리키면 라우트 이름으로 연다
      this.tabs.forEach(t => {
        const r = t.fullPath ? this.$router.resolve(t.fullPath).route : null;
        if (!r || r.name !== t.name) t.fullPath = this.$router.resolve({ name: t.name }).route.fullPath;
      });
    },
    savePinned() {
      try {
        const list = this.tabs.filter(t => t.pinned).map(t => ({ name: t.name, fullPath: t.fullPath }));
        localStorage.setItem(PIN_KEY, JSON.stringify(list));
      } catch (e) {
        console.error('[쿠패스 탭] 고정 탭 저장 실패', e);
      }
    }
  }
};
</script>

<style scoped>
.cp-tabbar {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 6px 0 8px;
  margin-bottom: 8px;
  border-bottom: 1px solid #e5e7eb;
  user-select: none;
}
.cp-tab {
  display: inline-flex;
  align-items: center;
  max-width: 220px;
  padding: 4px 8px 4px 10px;
  font-size: 13px;
  line-height: 20px;
  color: #475569;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 6px 6px 0 0;
  cursor: pointer;
}
.cp-tab:hover { background: #e2e8f0; }
.cp-tab--active {
  color: #fff;
  background: #3b82f6;
  border-color: #3b82f6;
}
.cp-tab--active:hover { background: #2563eb; }
.cp-tab__pin { font-size: 11px; margin-right: 4px; }
.cp-tab__title {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.cp-tab__close {
  margin-left: 6px;
  padding: 0 3px;
  font-size: 15px;
  line-height: 16px;
  border-radius: 3px;
  opacity: 0.7;
}
.cp-tab__close:hover { opacity: 1; background: rgba(0, 0, 0, 0.15); }
.cp-tabmenu {
  position: fixed;
  z-index: 2000;
  min-width: 170px;
  margin: 0;
  padding: 4px 0;
  list-style: none;
  font-size: 13px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.15);
}
.cp-tabmenu li {
  padding: 6px 14px;
  cursor: pointer;
}
.cp-tabmenu li:hover { background: #f1f5f9; }
</style>
